// src/modules/app-views/account/pages/Subscription/tabs/RecordTab/ResourcesCard.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useMemo } from 'react';
import { Card, Row, Col, theme, Tooltip, Badge } from 'antd';
import { CheckCircleFilled } from '@ant-design/icons';
import translateX from 'utils/translateX';
import app from 'configs/appResolver';
import { AVAILABLE_RESOURCES, getAllDependencies, getLockedDependencies } from 'constants/Resources';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const ResourcesCard = ({
  value = [],
  onChange,
  disabled = false,
  nativeResources = [],
  availableResources,
  colMd = 4,
}) => {
  // ---------------------------------------------------------------------------
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ---------------------------------------------------------------------------
  const { token } = theme.useToken();

  // ---------------------------------------------------------------------------
  // 2. HOOKS & REFS
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // 3. LOCAL VARIABLES I
  // ---------------------------------------------------------------------------
  // Filter resources by current application and environment, then by availableResources prop
  const currentEnv = app.HOMOLOGATION_MODE ? 'homologation' : 'production';
  const appResources = AVAILABLE_RESOURCES.filter(r =>
    r.availability?.[app.ID]?.includes(currentEnv)
  );
  const resources = availableResources?.length
    ? appResources.filter(r => availableResources.includes(r.uid))
    : appResources;

  // Compute which resources are locked because another selected resource requires them
  const lockedByDependency = useMemo(() => getLockedDependencies(value), [value]);

  // ---------------------------------------------------------------------------
  // 4. LOCAL STATE
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // 5. EFFECTS
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // 6. HANDLERS
  // ---------------------------------------------------------------------------
  const handleToggleResource = uid => {
    // Prevent interaction when fully disabled, native (locked), or locked by dependency
    if (disabled || nativeResources.includes(uid) || lockedByDependency.has(uid)) return;

    const isSelected = value.includes(uid);
    let newValue;

    if (isSelected) {
      // Deselect: just remove this UID, keep dependencies (unlock model)
      newValue = value.filter(u => u !== uid);
    } else {
      // Select: auto-add all transitive dependencies that are not already selected
      const deps = getAllDependencies(uid).filter(d => !value.includes(d));
      newValue = [...value, uid, ...deps];
    }

    onChange?.(newValue);
  };

  // ---------------------------------------------------------------------------
  // 7. LOCAL VARIABLES II
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // 8. UI
  // ---------------------------------------------------------------------------
  const resourceUids = new Set(resources.map(r => r.uid));
  const selectedCount = value.filter(uid => resourceUids.has(uid)).length;

  const cardTitle = (
    <div
      className="text-muted"
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
    >
      <span>
        <span style={{ color: 'red', marginRight: 6, fontSize: 12 }}>*</span>
        {translateX('system_resources')}
      </span>
      {selectedCount > 0 && (
        <Badge count={selectedCount} style={{ backgroundColor: token.colorPrimary }} />
      )}
    </div>
  );

  return (
    <Card id="resources-card" title={cardTitle} className="no-shadow">
      <Row gutter={[12, 12]} style={{ alignItems: 'stretch' }}>
          {resources.map(resource => {
            const isSelected = value.includes(resource.uid);
            const isNative = nativeResources.includes(resource.uid);
            const isLockedByDep = lockedByDependency.has(resource.uid);
            const isDisabled = disabled || isNative || isLockedByDep;
            const IconComponent = resource.icon;

            return (
              <Col xs={12} sm={8} md={colMd} key={resource.uid} style={{ display: 'flex' }}>
                <Tooltip title={translateX(resource.description)} style={{ display: 'flex', flex: 1 }}>
                  <div
                    onClick={() => handleToggleResource(resource.uid)}
                    style={buildCardStyle(isSelected, token, isDisabled)}
                  >
                    {/* check icon */}
                    {isSelected && (
                      <CheckCircleFilled style={buildCheckIconStyle(token, isDisabled)} />
                    )}

                    {/* icon */}
                    <IconComponent style={buildResourceIconStyle(token, isDisabled, isSelected)} />

                    {/* label */}
                    <div style={buildResourceLabelStyle(token, isDisabled, isSelected)}>
                      {translateX(resource.label)}
                    </div>
                  </div>
                </Tooltip>
              </Col>
            );
          })}
      </Row>
    </Card>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default ResourcesCard;
export { AVAILABLE_RESOURCES };

// *******************************************************************************
// HELPERS
// *******************************************************************************
function buildCheckIconStyle(token, isDisabled) {
  return { position: 'absolute', top: 8, right: 8, color: isDisabled ? token.colorTextDisabled : token.colorPrimary, fontSize: 16 };
}

function buildResourceIconStyle(token, isDisabled, isSelected) {
  const color = isDisabled ? token.colorTextDisabled : isSelected ? token.colorPrimary : token.colorTextSecondary;
  return { fontSize: 28, color, marginBottom: 8 };
}

function buildResourceLabelStyle(token, isDisabled, isSelected) {
  const color = isDisabled ? token.colorTextDisabled : isSelected ? token.colorText : token.colorTextSecondary;
  return { fontSize: 13, fontWeight: isSelected && !isDisabled ? 600 : 400, color, textAlign: 'center', lineHeight: 1.3 };
}

function buildCardStyle(isSelected, token, disabled) {
  // Disabled + selected (native resources): grey border/background instead of primary
  const borderColor = disabled
    ? token.colorBorderSecondary
    : isSelected
      ? token.colorPrimary
      : token.colorBorderSecondary;

  const bgColor = disabled
    ? token.colorFillTertiary
    : isSelected
      ? token.colorPrimaryBg
      : token.colorBgContainer;

  return {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px 8px',
    minHeight: 100,
    flex: 1,
    borderRadius: token.borderRadius,
    border: `1px solid ${borderColor}`,
    backgroundColor: bgColor,
    boxShadow: isSelected && !disabled ? `0 2px 8px ${token.colorPrimaryBgHover}` : 'none',
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'all 0.2s ease',
    userSelect: 'none',
  };
}
